import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserverResult } from 'react-query';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import { ProjectStateType } from '@components/profile/profileOverlay/overlays/project';
import ProjectName from '@components/profile/profileOverlay/overlays/project/projectForm/ProjectName';
import StartDate from '@components/profile/profileOverlay/overlays/project/projectForm/StartDate';
import EndDate from '@components/profile/profileOverlay/overlays/project/projectForm/EndDate';
import ProjectType from '@components/profile/profileOverlay/overlays/project/projectForm/ProjectType';
import Performance from '@components/profile/profileOverlay/overlays/project/projectForm/Performance';
import ProjectPlace from '@components/profile/profileOverlay/overlays/project/projectForm/ProjectPlace';
import SkillKeywordForm from '@components/profile/profileOverlay/overlays/project/projectForm/skillKeywordForm';
import projectFormSchema from '@components/profile/profileOverlay/overlays/project/projectForm/projectFormSchema';
import { useSaveProfileProjects } from '@repository/profiles/useProfileProject';
import { useMatchProfile } from '@repository/match';
import { ProfileProjectSaveVO } from '@domain/rq/profile/ProfileProjectListSaveRq';
import { SourcePage } from '@domain/rq/DataLog';
import ProfileProjectsVO, { ProfileProjectVO } from '@domain/vo/profile/ProfileProjectVO';
import { ProfileProjectFormVO } from '@domain/vo/profile/ProfileProjectFormVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from '../project.module.scss';
const cx = classnames.bind(styles);

const projectDefaultValues: ProfileProjectFormVO = {
  title: '',
  startDate: '',
  endDate: '',
  teamType: null,
  contribution: null,
  performance: '',
  institution: '',
  skills: [],
};

interface ProjectFormProps {
  refetch: () => Promise<QueryObserverResult>;
  profileProjects?: ProfileProjectsVO;
  project: ProfileProjectVO | null;
  projectIndex: number | null;
  setProjectState: (projectState: ProjectStateType) => void;
  openExitModal: () => void;
}

//활용 기술 포커싱 되었을 때 위치 이동용
export const projectFormName = 'ProjectForm';

const ProjectForm = forwardRef(
  ({ refetch, profileProjects, project, projectIndex, setProjectState, openExitModal }: ProjectFormProps, ref) => {
    const Toast = useToast();
    const { query } = useRouter();
    useImperativeHandle(ref, () => ({
      getIsDirty,
    }));

    const { refetch: matchProfileRefetch } = useMatchProfile();
    const methods = useForm<ProfileProjectFormVO>({
      mode: 'onChange',
      defaultValues: projectDefaultValues as ProfileProjectFormVO,
      resolver: yupResolver(projectFormSchema) as any,
    });

    const { handleSubmit, reset, formState } = methods;

    const { mutateAsync: saveProfileProject } = useSaveProfileProjects(() => {
      refetch().then(() => {
        Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
        setProjectState('LIST');
      });
    });

    useEffect(() => {
      if (project) reset(new ProfileProjectFormVO(project));
    }, [project]);

    const getIsDirty = (): boolean => {
      return formState.isDirty;
    };

    const handlePrev = () => {
      if (project && formState.isDirty) {
        openExitModal();
        return;
      }

      if (formState.isDirty) {
        openExitModal();
        return;
      }

      setProjectState('LIST');
    };

    const onSubmit: SubmitHandler<ProfileProjectFormVO> = async (vo) => {
      const getProjects = () => {
        const arr =
          profileProjects?.projects?.map((project) => ProfileProjectSaveVO.convertProfileProjectSaveVO(project)) || [];
        const newProject = ProfileProjectFormVO.convertProfileProjectSaveVO(vo);
        if (projectIndex === null) return [...arr, newProject];
        else
          return arr.map((project, index) => {
            if (index === projectIndex) return newProject;
            else return project;
          });
      };
      await saveProfileProject({ projects: getProjects(), sourcePage: query.source_page as SourcePage });
      await matchProfileRefetch();
    };

    const onSubmitError: SubmitErrorHandler<ProfileProjectFormVO> = (e) => {
      Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
    };

    return (
      <FormProvider {...methods}>
        <form
          id="careerForm"
          className={cx('scrollArea')}
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('scrollInner')} id={projectFormName}>
            <div className={cx('scrollWrap')}>
              <div className={cx('writeArea')}>
                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>프로젝트명</span>
                  {/* 프로젝트명 */}
                  <ProjectName name="title" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>프로젝트 기간</span>
                  {/* 프로젝트 기간 */}
                  <div className={cx('periodArea')}>
                    <StartDate name="startDate" />
                    <mark>~</mark>
                    <EndDate name="endDate" />
                  </div>
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>팀/개인</span>
                  {/* 프로젝트 타입 */}
                  <ProjectType name={['teamType', 'contribution']} />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText')}>주요 업무 및 성과</span>
                  {/* 주요 업무 및 성과 */}
                  <Performance name="performance" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText')}>학교/회사/기관</span>
                  {/* 프로젝트 진행 장소 */}
                  <ProjectPlace name="institution" />
                </div>

                <div className={cx('writeInner')}>
                  {/* 활용 기술 */}
                  <SkillKeywordForm name="skills" />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className={cx('btnArea')}>
          <a className={cx('btn', 'cancel')} role="button" onClick={handlePrev}>
            이전
          </a>
          <button className={cx('btn', 'confirm')} type="submit" form="careerForm">
            등록
          </button>
        </div>
      </FormProvider>
    );
  },
);

export default ProjectForm;
